import { memo, useCallback, useMemo, useState } from 'react'
import { AlertTriangle, ChevronDown, Check, Loader2, Wrench } from 'lucide-react'
import { AnimatePresence, motion, Transition, Variants } from 'framer-motion'
import { CodeBlock } from '../../components'

interface ManageSkillToolProps {
  result: string | null
  input: Record<string, unknown> | string | null
  isLoading: boolean
  error?: string | null
  messageId: string
}

interface ParsedValue {
  parsed: unknown
  raw: string | null
  isJson: boolean
}

type SkillAction = 'list' | 'get' | 'create' | 'update' | 'fork'

const detailsVariants: Variants = {
  collapsed: { height: 0, opacity: 0 },
  expanded: { height: 'auto', opacity: 1 },
}

const detailsTransition: Transition = {
  height: { duration: 0.2, ease: 'easeInOut' },
  opacity: { duration: 0.15, ease: 'easeInOut' },
}

const chevronVariants: Variants = {
  collapsed: { rotate: 0 },
  expanded: { rotate: 180 },
}

const chevronTransition: Transition = { duration: 0.2, ease: 'easeInOut' }

const loadingContainerClassName =
  'bg-background-subtle/50 shadow-sm flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm'

const successContainerClassName = 'bg-background-subtle/50 shadow-sm rounded-xl text-sm'
const errorContainerClassName = 'bg-background-subtle/50 border-danger shadow-sm rounded-xl border text-sm'

const successButtonClassName =
  'bg-transparent hover:bg-foreground/[0.03] flex w-full items-start gap-2.5 rounded-xl px-3 py-2.5 text-left transition-colors'
const errorButtonClassName =
  'bg-transparent hover:bg-danger/[0.04] flex w-full items-start gap-2.5 rounded-xl px-3 py-2.5 text-left transition-colors'

const successIconWrapperClassName =
  'bg-success/15 ring-success/15 mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full ring-1'
const errorIconWrapperClassName =
  'bg-danger/10 ring-danger/10 mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full ring-1'

const skillBadgeClassName =
  'bg-background text-foreground-secondary mt-1 inline-flex w-fit items-center gap-1 rounded-md px-1.5 py-1 text-[11px] shadow-sm'

function parseValue(value: Record<string, unknown> | string | null): ParsedValue {
  if (!value) {
    return {
      parsed: null,
      raw: null,
      isJson: false,
    }
  }

  if (typeof value === 'string') {
    try {
      return {
        parsed: JSON.parse(value),
        raw: value,
        isJson: true,
      }
    } catch {
      return {
        parsed: null,
        raw: value,
        isJson: false,
      }
    }
  }

  return {
    parsed: value,
    raw: JSON.stringify(value, null, 2),
    isJson: true,
  }
}

function getObjectValue(source: unknown, key: string): unknown {
  if (!source || typeof source !== 'object') return undefined
  return (source as Record<string, unknown>)[key]
}

function getFirstString(source: unknown, keys: Array<string>): string | null {
  for (const key of keys) {
    const value = getObjectValue(source, key)
    if (typeof value === 'string' && value.trim()) {
      return value.trim()
    }
  }

  return null
}

function getNestedString(source: unknown, parentKey: string, childKeys: Array<string>): string | null {
  const parent = getObjectValue(source, parentKey)
  return getFirstString(parent, childKeys)
}

function isUuidLike(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)
}

function getFirstHumanLabel(source: unknown, preferredKeys: Array<string>, fallbackKeys: Array<string>): string | null {
  const preferredLabel = getFirstString(source, preferredKeys)
  if (preferredLabel) {
    return preferredLabel
  }

  const fallbackLabel = getFirstString(source, fallbackKeys)
  if (fallbackLabel && !isUuidLike(fallbackLabel)) {
    return fallbackLabel
  }

  return null
}

function getSkillAction(input: ParsedValue): SkillAction | null {
  const action =
    getFirstString(input.parsed, ['action']) ||
    getNestedString(input.parsed, 'request', ['action']) ||
    getNestedString(input.parsed, 'input', ['action'])

  switch (action) {
    case 'list':
    case 'get':
    case 'create':
    case 'update':
    case 'fork':
      return action
    default:
      return null
  }
}

function getSkillCount(result: ParsedValue): number | null {
  if (Array.isArray(result.parsed)) {
    return result.parsed.length
  }

  const resultData = getObjectValue(result.parsed, 'data')
  if (Array.isArray(resultData)) {
    return resultData.length
  }

  const skills = getObjectValue(result.parsed, 'skills')
  if (Array.isArray(skills)) {
    return skills.length
  }

  const nestedResult = getObjectValue(result.parsed, 'result')
  if (Array.isArray(nestedResult)) {
    return nestedResult.length
  }

  const nestedResultData = getObjectValue(nestedResult, 'data')
  if (Array.isArray(nestedResultData)) {
    return nestedResultData.length
  }

  return null
}

function getSummary(
  action: SkillAction | null,
  result: ParsedValue,
  skillLabel: string | null,
  fallbackError?: string | null,
): string {
  const structuredError = getFirstString(result.parsed, ['error'])
  if (fallbackError || structuredError) {
    return structuredError || fallbackError || 'The skill request did not complete.'
  }

  const structuredMessage =
    getFirstString(result.parsed, ['message', 'summary', 'description']) ||
    getNestedString(result.parsed, 'result', ['message', 'summary', 'description'])

  if (structuredMessage) {
    return structuredMessage
  }

  if (result.raw && !result.isJson) {
    return result.raw
  }

  switch (action) {
    case 'list': {
      const skillCount = getSkillCount(result)
      return skillCount === null
        ? 'Retrieved the available skills.'
        : `Found ${skillCount} ${skillCount === 1 ? 'skill' : 'skills'}.`
    }
    case 'get':
      return skillLabel ? `Loaded ${skillLabel}.` : 'Loaded the requested skill.'
    case 'create':
      return skillLabel ? `Created ${skillLabel}.` : 'Created a new skill.'
    case 'update':
      return skillLabel ? `Updated ${skillLabel}.` : 'Updated the requested skill.'
    case 'fork':
      return skillLabel ? `Forked ${skillLabel}.` : 'Forked the requested skill.'
    default:
      return 'The skill request completed successfully.'
  }
}

function getSkillLabel(input: ParsedValue, result: ParsedValue): string | null {
  return (
    getFirstHumanLabel(input.parsed, ['skillName', 'name'], ['slug', 'skillId', 'id']) ||
    getFirstHumanLabel(getObjectValue(input.parsed, 'skill'), ['name'], ['slug', 'id']) ||
    getFirstHumanLabel(getObjectValue(input.parsed, 'data'), ['name'], ['slug', 'id']) ||
    getFirstHumanLabel(getObjectValue(input.parsed, 'result'), ['name'], ['slug', 'id']) ||
    getFirstHumanLabel(result.parsed, ['skillName', 'name'], ['slug', 'skillId', 'id']) ||
    getFirstHumanLabel(getObjectValue(result.parsed, 'skill'), ['name'], ['slug', 'id']) ||
    getFirstHumanLabel(getObjectValue(result.parsed, 'data'), ['name'], ['slug', 'id']) ||
    getFirstHumanLabel(getObjectValue(result.parsed, 'result'), ['name'], ['slug', 'id'])
  )
}

function getStatusCopy(action: SkillAction | null, hasError: boolean): string {
  switch (action) {
    case 'list':
      return hasError ? 'Failed to list skills' : 'Skills listed'
    case 'get':
      return hasError ? 'Failed to load skill' : 'Skill loaded'
    case 'create':
      return hasError ? 'Failed to create skill' : 'Skill created'
    case 'update':
      return hasError ? 'Failed to update skill' : 'Skill updated'
    case 'fork':
      return hasError ? 'Failed to fork skill' : 'Skill forked'
    default:
      return hasError ? 'Skill request failed' : 'Skill request completed'
  }
}

function getLoadingCopy(action: SkillAction | null): string {
  switch (action) {
    case 'list':
      return 'Listing skills...'
    case 'get':
      return 'Loading skill...'
    case 'create':
      return 'Creating skill...'
    case 'update':
      return 'Updating skill...'
    case 'fork':
      return 'Forking skill...'
    default:
      return 'Managing skill...'
  }
}

function stringifyValue(value: ParsedValue): string | null {
  if (value.isJson) {
    try {
      return JSON.stringify(value.parsed, null, 2)
    } catch {
      return value.raw
    }
  }

  return value.raw
}

export const ManageSkillTool = memo(({ result, input, isLoading, error, messageId }: ManageSkillToolProps) => {
  const [isExpanded, setIsExpanded] = useState(false)

  const toggleExpanded = useCallback(() => setIsExpanded((prev) => !prev), [])

  const parsedInput = useMemo(() => parseValue(input), [input])
  const parsedResult = useMemo(() => parseValue(result), [result])
  const action = useMemo(() => getSkillAction(parsedInput), [parsedInput])

  const structuredError = getFirstString(parsedResult.parsed, ['error'])
  const hasError = Boolean(error || structuredError)
  const skillLabel = useMemo(() => getSkillLabel(parsedInput, parsedResult), [parsedInput, parsedResult])
  const summary = useMemo(
    () => getSummary(action, parsedResult, skillLabel, error),
    [action, parsedResult, skillLabel, error],
  )
  const statusCopy = useMemo(() => getStatusCopy(action, hasError), [action, hasError])
  const loadingCopy = useMemo(() => getLoadingCopy(action), [action])
  const inputContent = useMemo(() => stringifyValue(parsedInput), [parsedInput])
  const resultContent = useMemo(() => stringifyValue(parsedResult), [parsedResult])

  if (isLoading) {
    return (
      <div className={loadingContainerClassName}>
        <Loader2 className="text-success-subtle size-4 animate-spin" />
        <span className="text-foreground-secondary">{loadingCopy}</span>
      </div>
    )
  }

  return (
    <div className={hasError ? errorContainerClassName : successContainerClassName}>
      <button
        type="button"
        className={hasError ? errorButtonClassName : successButtonClassName}
        onClick={toggleExpanded}
        aria-expanded={isExpanded}
      >
        <div className={hasError ? errorIconWrapperClassName : successIconWrapperClassName}>
          {hasError ? (
            <AlertTriangle className="text-danger-subtle size-3" strokeWidth={2.5} />
          ) : (
            <Check className="text-success-subtle size-3" strokeWidth={2.5} />
          )}
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-foreground font-medium">{statusCopy}</span>
          <span className="text-foreground-secondary whitespace-pre-wrap text-xs">{summary}</span>
          {skillLabel && (
            <div className={skillBadgeClassName}>
              <Wrench className="size-3" />
              <span className="max-w-[260px] truncate">{skillLabel}</span>
            </div>
          )}
        </div>

        <motion.div
          variants={chevronVariants}
          animate={isExpanded ? 'expanded' : 'collapsed'}
          transition={chevronTransition}
          className="shrink-0"
        >
          <ChevronDown className="text-foreground-secondary size-4" />
        </motion.div>
      </button>

      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            variants={detailsVariants}
            initial="collapsed"
            animate="expanded"
            exit="collapsed"
            transition={detailsTransition}
            className="overflow-hidden"
          >
            <div className="mx-3 mb-3 flex flex-col gap-2 border-t pt-3 [&>*]:my-2">
              {inputContent && (
                <CodeBlock
                  title="Skill Request"
                  language={parsedInput.isJson ? 'json' : 'plaintext'}
                  hideTypeInApp
                  messageId={messageId}
                  wrapLines
                  wrapLongLines
                  maxHeight="300px"
                  truncate
                >
                  {inputContent}
                </CodeBlock>
              )}

              {(resultContent || hasError) && (
                <CodeBlock
                  title={hasError ? 'Skill Error' : 'Skill Result'}
                  language={parsedResult.isJson ? 'json' : 'plaintext'}
                  hideTypeInApp
                  messageId={messageId}
                  wrapLines
                  wrapLongLines
                  maxHeight="300px"
                  truncate
                >
                  {resultContent || structuredError || error || ''}
                </CodeBlock>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
})

ManageSkillTool.displayName = 'ManageSkillTool'
